import type { Currency } from '@vy/types';
import { useMemo } from 'react';
import { useDynamicInvoice } from '../../../dynamic-invoice-context';
import { nullToUndefined } from '@vy/null';
import { isNotNullOrUndefined } from '@vy/utils';
import { roundTo } from '@vy/number';

export type ExchangeRateData = {
  isExchangeRateFeatureActive: boolean;
  hasExchangeRate: boolean;
  contractCurrency: Currency | undefined;
  invoiceCurrency: Currency | undefined;
  exchangeRate: number;
  rateDate?: Date;
  formattedExchangeRate: string;
};

export const useExchangeRateData = (): ExchangeRateData => {
  const { dynamicInvoiceData } = useDynamicInvoice();

  const { invoice } = dynamicInvoiceData;

  const isExchangeRateFeatureActive = isNotNullOrUndefined(dynamicInvoiceData.contract?.currencyOverride);

  const { modifiedExchangeRate, exchangeRateSnapshot, exchangeRateSnapshotTimestamp } = invoice;

  const contractCurrency = nullToUndefined(invoice.baseCurrency);
  const invoiceCurrency = invoice.modifiedCurrency ?? invoice.currency;

  const hasExchangeRate = isNotNullOrUndefined(invoice.modifiedCurrency);

  const matrixRate =
    exchangeRateSnapshot && contractCurrency ? (exchangeRateSnapshot[contractCurrency] ?? 1) : 1;
  const invertedMatrixRate = 1 / matrixRate;
  const roundedInvertedMatrixRate = roundTo(invertedMatrixRate, 5);
  const exchangeRate = modifiedExchangeRate ? roundTo(modifiedExchangeRate, 5) : roundedInvertedMatrixRate;

  const rateDate = nullToUndefined(exchangeRateSnapshotTimestamp);

  const formattedExchangeRate =
    contractCurrency === invoiceCurrency || !contractCurrency
      ? 'N/A'
      : `1 ${contractCurrency} = ${exchangeRate} ${invoiceCurrency}`;

  const exchangeRateData = useMemo<ExchangeRateData>(
    () => ({
      isExchangeRateFeatureActive,
      contractCurrency,
      invoiceCurrency,
      exchangeRate,
      rateDate,
      formattedExchangeRate,
      hasExchangeRate,
    }),
    [
      isExchangeRateFeatureActive,
      contractCurrency,
      invoiceCurrency,
      exchangeRate,
      formattedExchangeRate,
      hasExchangeRate,
      rateDate,
    ],
  );

  return exchangeRateData;
};
